/**
 * AttachmentViewer - Modal component for viewing different file types
 * Supports: images (with zoom/rotate), PDFs, text/code/markdown, video/audio
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import * as Dialog from '@radix-ui/react-dialog';
import {
  X,
  Download,
  ZoomIn,
  ZoomOut,
  RotateCw,
  ChevronLeft,
  ChevronRight,
  Loader2,
  FileText,
  AlertCircle,
  Image as ImageIcon,
  Maximize2,
} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
// Use the async-light Prism build: the highlighter core and each language
// grammar are code-split and loaded on demand, instead of eagerly bundling
// the full Prism build (every language) into the board chunk.
import SyntaxHighlighter from 'react-syntax-highlighter/dist/esm/prism-async-light';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Attachment } from '@services/api';
import { useAuthStore } from '@stores/authStore';

// Configure PDF.js worker - use static file from public folder for reliability
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface AttachmentViewerProps {
  attachment: Attachment | null;
  attachments?: Attachment[]; // For navigation between attachments
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: (attachment: Attachment) => void;
  getAttachmentUrl: (id: string) => Promise<string>;
}

type ViewerType = 'image' | 'pdf' | 'text' | 'code' | 'markdown' | 'video' | 'audio' | 'unsupported';

// Map file extensions to syntax highlighter languages
const extensionToLanguage: Record<string, string> = {
  js: 'javascript',
  jsx: 'jsx',
  ts: 'typescript',
  tsx: 'tsx',
  py: 'python',
  rb: 'ruby',
  java: 'java',
  kt: 'kotlin',
  go: 'go',
  rs: 'rust',
  c: 'c',
  cpp: 'cpp',
  h: 'c',
  hpp: 'cpp',
  cs: 'csharp',
  php: 'php',
  swift: 'swift',
  sql: 'sql',
  sh: 'bash',
  bash: 'bash',
  zsh: 'bash',
  yaml: 'yaml',
  yml: 'yaml',
  json: 'json',
  // xml/html are aliases of Prism's "markup" grammar (the async-light loader keys by canonical name)
  xml: 'markup',
  html: 'markup',
  css: 'css',
  scss: 'scss',
  less: 'less',
  graphql: 'graphql',
  // dockerfile is an alias of Prism's "docker" grammar
  dockerfile: 'docker',
  makefile: 'makefile',
};

function getViewerType(contentType: string, filename: string): ViewerType {
  // Images
  if (contentType.startsWith('image/')) return 'image';

  // Videos
  if (contentType.startsWith('video/')) return 'video';

  // Audio
  if (contentType.startsWith('audio/')) return 'audio';

  // PDFs
  if (contentType === 'application/pdf') return 'pdf';

  // Markdown
  if (contentType === 'text/markdown' || filename.endsWith('.md') || filename.endsWith('.mdx')) {
    return 'markdown';
  }

  // Code files (check extension)
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  if (extensionToLanguage[ext]) return 'code';

  // Plain text and other text types
  if (contentType.startsWith('text/')) return 'text';

  // JSON, XML as code
  if (contentType === 'application/json' || contentType === 'application/xml') return 'code';

  return 'unsupported';
}

function getLanguageFromFilename(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return extensionToLanguage[ext] || 'text';
}

export default function AttachmentViewer({
  attachment,
  attachments = [],
  isOpen,
  onClose,
  onNavigate,
  getAttachmentUrl,
}: AttachmentViewerProps) {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);

  // PDF state
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfScale, setPdfScale] = useState(1.0);

  // Image state
  const [imageScale, setImageScale] = useState(1.0);
  const [imageRotation, setImageRotation] = useState(0);
  const [imageFit, setImageFit] = useState<'contain' | 'actual'>('contain');

  const viewerType = attachment ? getViewerType(attachment.contentType, attachment.filename) : 'unsupported';

  // Find current index for navigation
  const currentIndex = attachments.findIndex(a => a.id === attachment?.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < attachments.length - 1;

  // Load attachment content with auth
  useEffect(() => {
    if (!attachment || !isOpen) {
      setUrl(null);
      setTextContent(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let blobUrl: string | null = null;

    const loadAttachment = async () => {
      setLoading(true);
      setError(null);
      setTextContent(null);

      try {
        // Get the download URL from the API
        const attachmentUrl = await getAttachmentUrl(attachment.id);
        if (cancelled) return;

        // Fetch the actual file content with auth headers
        const { accessToken } = useAuthStore.getState();
        const response = await fetch(attachmentUrl, {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        });

        if (!response.ok) {
          // Try to get error detail from response body
          let errorDetail = '';
          try {
            const errorData = await response.json() as { detail?: string };
            errorDetail = errorData.detail || '';
          } catch {
            // Ignore JSON parse errors
          }
          throw new Error(errorDetail || `Failed to load file: ${response.status} ${response.statusText}`);
        }

        const type = getViewerType(attachment.contentType, attachment.filename);

        // For text-based files, get the text content
        if (type === 'text' || type === 'code' || type === 'markdown') {
          const text = await response.text();
          if (cancelled) return;
          setTextContent(text);
          setUrl(attachmentUrl); // Keep URL for reference
        } else {
          // For binary files (images, PDFs, video, audio), create a blob URL
          const blob = await response.blob();
          if (cancelled) return;
          blobUrl = URL.createObjectURL(blob);
          setUrl(blobUrl);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load attachment');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadAttachment();

    return () => {
      cancelled = true;
      // Clean up blob URL when component unmounts or attachment changes
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [attachment, isOpen, getAttachmentUrl]);

  // Reset PDF and image state when attachment changes
  useEffect(() => {
    setCurrentPage(1);
    setPdfScale(1.0);
    setImageScale(1.0);
    setImageRotation(0);
    setImageFit('contain');
  }, [attachment?.id]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && hasPrev && onNavigate) {
        const prevAttachment = attachments[currentIndex - 1];
        if (prevAttachment) onNavigate(prevAttachment);
      } else if (e.key === 'ArrowRight' && hasNext && onNavigate) {
        const nextAttachment = attachments[currentIndex + 1];
        if (nextAttachment) onNavigate(nextAttachment);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, hasPrev, hasNext, currentIndex, attachments, onNavigate, onClose]);

  const handleDownload = useCallback(() => {
    if (url && attachment) {
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename;
      a.click();
    }
  }, [url, attachment]);

  const handlePdfLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  if (!attachment) return null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={open => !open && onClose()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[80] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <Dialog.Content
            aria-describedby={undefined}
            className={clsx(
              'fixed left-[50%] top-[50%] z-[81] translate-x-[-50%] translate-y-[-50%]',
              'w-[95vw] max-w-6xl h-[90vh]',
              'bg-white dark:bg-neutral-900 rounded-xl shadow-2xl',
              'flex flex-col overflow-hidden',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
              'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]'
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-700">
              <div className="flex items-center gap-3 min-w-0">
                {viewerType === 'image' ? (
                  <ImageIcon className="h-5 w-5 text-neutral-500 flex-shrink-0" />
                ) : (
                  <FileText className="h-5 w-5 text-neutral-500 flex-shrink-0" />
                )}
                <Dialog.Title className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                  {attachment.filename}
                </Dialog.Title>
              </div>

              <div className="flex items-center gap-2">
                {/* PDF controls */}
                {viewerType === 'pdf' && numPages > 0 && (
                  <>
                    <button
                      onClick={() => setPdfScale(s => Math.max(0.5, s - 0.25))}
                      className="p-1.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400"
                      title={t('attachment.zoomOut')}
                    >
                      <ZoomOut className="h-4 w-4" />
                    </button>
                    <span className="text-xs text-neutral-500 w-12 text-center">
                      {Math.round(pdfScale * 100)}%
                    </span>
                    <button
                      onClick={() => setPdfScale(s => Math.min(3, s + 0.25))}
                      className="p-1.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400"
                      title={t('attachment.zoomIn')}
                    >
                      <ZoomIn className="h-4 w-4" />
                    </button>
                    <div className="w-px h-4 bg-neutral-200 dark:bg-neutral-700 mx-1" />
                    <span className="text-xs text-neutral-500">
                      {currentPage} / {numPages}
                    </span>
                    <div className="w-px h-4 bg-neutral-200 dark:bg-neutral-700 mx-1" />
                  </>
                )}

                {/* Image controls */}
                {viewerType === 'image' && url && (
                  <>
                    <button
                      onClick={() => setImageScale(s => Math.max(0.25, s - 0.25))}
                      className="p-1.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400"
                      title={t('attachment.zoomOut')}
                    >
                      <ZoomOut className="h-4 w-4" />
                    </button>
                    <span className="text-xs text-neutral-500 w-12 text-center">
                      {Math.round(imageScale * 100)}%
                    </span>
                    <button
                      onClick={() => setImageScale(s => Math.min(5, s + 0.25))}
                      className="p-1.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400"
                      title={t('attachment.zoomIn')}
                    >
                      <ZoomIn className="h-4 w-4" />
                    </button>
                    <div className="w-px h-4 bg-neutral-200 dark:bg-neutral-700 mx-1" />
                    <button
                      onClick={() => setImageFit(f => f === 'contain' ? 'actual' : 'contain')}
                      className={clsx(
                        'p-1.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800',
                        imageFit === 'actual' ? 'text-primary-600' : 'text-neutral-600 dark:text-neutral-400'
                      )}
                      title={imageFit === 'contain' ? t('attachment.actualSize') : t('attachment.fitScreen')}
                    >
                      <Maximize2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setImageRotation(r => (r + 90) % 360)}
                      className="p-1.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400"
                      title={t('attachment.rotate')}
                    >
                      <RotateCw className="h-4 w-4" />
                    </button>
                    <div className="w-px h-4 bg-neutral-200 dark:bg-neutral-700 mx-1" />
                  </>
                )}

                {/* Download button */}
                <button
                  onClick={handleDownload}
                  className="p-1.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400"
                  title={t('attachment.download')}
                >
                  <Download className="h-4 w-4" />
                </button>

                {/* Close button */}
                <Dialog.Close asChild>
                  <button
                    className="p-1.5 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400"
                    title={t('attachment.close')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </Dialog.Close>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto relative">
              {loading ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                </div>
              ) : error ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-neutral-500">
                  <AlertCircle className="h-12 w-12 mb-3 text-red-500" />
                  <p className="text-sm">{error}</p>
                </div>
              ) : (
                <>
                  {/* PDF Viewer */}
                  {viewerType === 'pdf' && url && (
                    <div className="flex justify-center p-4 min-h-full bg-neutral-100 dark:bg-neutral-800">
                      <Document
                        file={url}
                        onLoadSuccess={handlePdfLoadSuccess}
                        loading={
                          <div className="flex items-center justify-center py-20">
                            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                          </div>
                        }
                        error={
                          <div className="flex flex-col items-center justify-center py-20 text-red-500">
                            <AlertCircle className="h-12 w-12 mb-3" />
                            <p>Failed to load PDF</p>
                          </div>
                        }
                      >
                        {Array.from({ length: numPages }, (_, i) => (
                          <Page
                            key={i + 1}
                            pageNumber={i + 1}
                            scale={pdfScale}
                            className="mb-4 shadow-lg"
                            onRenderSuccess={() => {
                              if (i + 1 === currentPage) {
                                // Page rendered
                              }
                            }}
                          />
                        ))}
                      </Document>
                    </div>
                  )}

                  {/* Image Viewer */}
                  {viewerType === 'image' && url && (
                    <div className="flex items-center justify-center h-full bg-neutral-900 overflow-auto p-4">
                      <img
                        src={url}
                        alt={attachment.filename}
                        className="transition-transform duration-200"
                        style={{
                          transform: `scale(${imageScale}) rotate(${imageRotation}deg)`,
                          maxWidth: imageFit === 'contain' ? '100%' : 'none',
                          maxHeight: imageFit === 'contain' ? '100%' : 'none',
                          objectFit: imageFit === 'contain' ? 'contain' : 'none',
                        }}
                        draggable={false}
                      />
                    </div>
                  )}

                  {/* Text Viewer */}
                  {viewerType === 'text' && textContent !== null && (
                    <pre className="p-4 text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap font-mono">
                      {textContent}
                    </pre>
                  )}

                  {/* Code Viewer */}
                  {viewerType === 'code' && textContent !== null && (
                    <SyntaxHighlighter
                      language={getLanguageFromFilename(attachment.filename)}
                      style={oneDark}
                      showLineNumbers
                      customStyle={{
                        margin: 0,
                        borderRadius: 0,
                        minHeight: '100%',
                        fontSize: '13px',
                      }}
                    >
                      {textContent}
                    </SyntaxHighlighter>
                  )}

                  {/* Markdown Viewer */}
                  {viewerType === 'markdown' && textContent !== null && (
                    <div className="p-6 prose prose-neutral dark:prose-invert max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {textContent}
                      </ReactMarkdown>
                    </div>
                  )}

                  {/* Video Player */}
                  {viewerType === 'video' && url && (
                    <div className="flex items-center justify-center h-full bg-black p-4">
                      <video
                        src={url}
                        controls
                        autoPlay={false}
                        className="max-w-full max-h-full"
                      >
                        {t('attachment.videoNotSupported')}
                      </video>
                    </div>
                  )}

                  {/* Audio Player */}
                  {viewerType === 'audio' && url && (
                    <div className="flex flex-col items-center justify-center h-full p-8">
                      <div className="w-full max-w-md p-6 bg-neutral-100 dark:bg-neutral-800 rounded-xl">
                        <p className="text-center text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                          {attachment.filename}
                        </p>
                        <audio src={url} controls className="w-full">
                          {t('attachment.audioNotSupported')}
                        </audio>
                      </div>
                    </div>
                  )}

                  {/* Unsupported */}
                  {viewerType === 'unsupported' && (
                    <div className="flex flex-col items-center justify-center h-full text-neutral-500">
                      <FileText className="h-16 w-16 mb-4" />
                      <p className="text-lg font-medium mb-2">{t('attachment.unsupportedFile')}</p>
                      <p className="text-sm mb-4">{t('attachment.downloadToView')}</p>
                      <button
                        onClick={handleDownload}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
                      >
                        <Download className="h-4 w-4" />
                        {t('attachment.download')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Navigation arrows */}
            {attachments.length > 1 && (
              <>
                {hasPrev && (
                  <button
                    onClick={() => {
                      const prevAttachment = attachments[currentIndex - 1];
                      if (prevAttachment) onNavigate?.(prevAttachment);
                    }}
                    className={clsx(
                      'absolute left-2 top-1/2 -translate-y-1/2 z-10',
                      'p-2 rounded-full bg-black/50 text-white',
                      'hover:bg-black/70 transition-colors'
                    )}
                    title={t('attachment.previous')}
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                )}
                {hasNext && (
                  <button
                    onClick={() => {
                      const nextAttachment = attachments[currentIndex + 1];
                      if (nextAttachment) onNavigate?.(nextAttachment);
                    }}
                    className={clsx(
                      'absolute right-2 top-1/2 -translate-y-1/2 z-10',
                      'p-2 rounded-full bg-black/50 text-white',
                      'hover:bg-black/70 transition-colors'
                    )}
                    title={t('attachment.next')}
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                )}
              </>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
  );
}
